/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Typed endpoint wrappers. Views never build a URL by hand.
 */

import { api } from "./client.ts";
import type { ModelSettings } from "./chef.ts";
import type { YMD } from "../lib/dates.ts";
import type {
  AcceptedShare, CookSession, CreatedShare, DietProfile, Food, Household,
  IncomingShare, ItemSource, MealSlot, OutgoingShare, PantryStaple,
  PlanConflict, PlanEntry, PlanResponse, Recipe, RecipeDraft, RecipeSummary,
  RecipeComment, RegenerateDiff, ScaledRecipe, Settings, SharePreview, ShoppingItem,
  ThreadAvailability, ThreadComment, ThreadDelta, ThreadListEntry, ThreadSummary,
  ShoppingList, Store, SyncResponse, TodayPayload, HomeResponse, HomeBrief,
  DietOptionsResponse, RecipeConflicts,
  CirclesSettings, Circle, CircleSummary, CircleMember, CircleMessage,
  CircleInvite, CircleDelta, CreatedInvite, PostedMessage,
  ReactionKind, ReactionSummary,
  CircleLibrarySettings, AvailableLibraryRecipe,
  NutritionPanel, NutritionStatus,
  DisplayDevice, PendingDisplayCode,
} from "./types.ts";

export const Meta = {
  today: () => api.get<TodayPayload>("/api/today"),
  health: () => api.get<{ ok: boolean; db: string }>("/api/v1/health"),
};

export const HouseholdApi = {
  get: () => api.get<Household>("/api/household"),
  setName: (displayName: string) => api.patch("/api/household/me", { displayName }),
  invite: () => api.post<{ code: string; expiresAt: string }>("/api/household/invite"),
  invites: () => api.get<{ invites: unknown[] }>("/api/household/invites"),
  revoke: (id: string) => api.del(`/api/household/invite/${id}`),
  join: (code: string) => api.post<{ ok: true }>("/api/household/join", { code }),
  removeMember: (sub: string) => api.del(`/api/household/member/${encodeURIComponent(sub)}`),
  leave: (confirmDeleteEverything = false) =>
    api.post<{ ok: true; deletedPrevious: boolean }>("/api/household/leave",
      { confirmDeleteEverything }),
};

export const SettingsApi = {
  get: () => api.get<{
    settings: Settings; slots: MealSlot[]; diets: DietProfile[]; stores: Store[]; today: YMD;
    /** Model pickers, served as data. See ModelSettings in api/chef.ts. */
    models: ModelSettings;
  }>("/api/settings"),
  update: (body: Record<string, unknown>) => api.patch<Settings>("/api/settings", body),

  addSlot: (body: Record<string, unknown>) => api.post<MealSlot>("/api/settings/slots", body),
  updateSlot: (id: string, body: Record<string, unknown>) =>
    api.patch<MealSlot>(`/api/settings/slots/${id}`, body),
  removeSlot: (id: string) => api.del(`/api/settings/slots/${id}`),

  addDiet: (body: Record<string, unknown>) => api.post<DietProfile>("/api/settings/diets", body),
  updateDiet: (id: string, body: Record<string, unknown>) =>
    api.patch<DietProfile>(`/api/settings/diets/${id}`, body),
  removeDiet: (id: string) => api.del(`/api/settings/diets/${id}`),

  addStore: (body: Record<string, unknown>) => api.post<Store>("/api/settings/stores", body),
  updateStore: (id: string, body: Record<string, unknown>) =>
    api.patch<Store>(`/api/settings/stores/${id}`, body),
  removeStore: (id: string) => api.del(`/api/settings/stores/${id}`),
};

/**
 * Raspberry Pi displays.
 *
 * Note what is NOT here: there is no wrapper for /display/pair or /display/day.
 * Those are the DEVICE's endpoints, authenticated with a device token rather
 * than the browser's Cognito session, and the web app has no business calling
 * them — see server/routes_display.ts.
 */
export const DisplaysApi = {
  list: () => api.get<{ devices: DisplayDevice[]; pendingCodes: PendingDisplayCode[] }>(
    "/api/settings/displays"),
  /** The raw code is in this response and nowhere else — it cannot be re-shown. */
  createCode: () => api.post<{ code: string; expiresAt: string }>(
    "/api/settings/displays/codes"),
  cancelCode: (id: string) => api.del(`/api/settings/displays/codes/${id}`),
  revoke: (id: string) => api.del(`/api/settings/displays/${id}`),
};

export const FoodsApi = {
  search: (q: string, limit = 25) =>
    api.get<{ foods: Food[] }>(`/api/foods?q=${encodeURIComponent(q)}&limit=${limit}`),
  aisles: () => api.get<{ aisles: string[] }>("/api/foods/aisles"),
  create: (body: Record<string, unknown>) => api.post<Food>("/api/foods", body),
  update: (id: string, body: Record<string, unknown>) => api.patch<Food>(`/api/foods/${id}`, body),
  remove: (id: string) => api.del(`/api/foods/${id}`),
  match: (names: string[]) =>
    api.post<{ matches: Array<{ input: string; foodId: string | null; confidence: number }>; needsReview: number }>(
      "/api/foods/match", { names }),
};

export interface RecipeFilters {
  q?: string;
  course?: string;
  cuisine?: string;
  maxMinutes?: number;
  diet?: string[];
  excludeAllergen?: string[];
  favorite?: boolean;
  includeArchived?: boolean;
  sort?: "recent" | "title" | "quickest" | "mostCooked" | "leastRecentlyCooked";
  limit?: number;
  offset?: number;
}

function qs(f: RecipeFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.course) p.set("course", f.course);
  if (f.cuisine) p.set("cuisine", f.cuisine);
  if (f.maxMinutes) p.set("maxMinutes", String(f.maxMinutes));
  if (f.diet?.length) p.set("diet", f.diet.join(","));
  if (f.excludeAllergen?.length) p.set("excludeAllergen", f.excludeAllergen.join(","));
  if (f.favorite) p.set("favorite", "true");
  if (f.includeArchived) p.set("includeArchived", "true");
  if (f.sort) p.set("sort", f.sort);
  if (f.limit) p.set("limit", String(f.limit));
  if (f.offset) p.set("offset", String(f.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const RecipesApi = {
  list: (f: RecipeFilters = {}) =>
    api.get<{ recipes: RecipeSummary[]; today: YMD }>(`/api/recipes${qs(f)}`),
  get: (id: string) => api.get<Recipe>(`/api/recipes/${id}`),
  create: (body: Record<string, unknown>) => api.post<Recipe>("/api/recipes", body),
  replace: (id: string, body: Record<string, unknown>) => api.put<Recipe>(`/api/recipes/${id}`, body),
  patch: (id: string, body: Record<string, unknown>) => api.patch<Recipe>(`/api/recipes/${id}`, body),
  remove: (id: string, hard = false) =>
    api.del<{ ok: true; archived?: boolean; deleted?: boolean }>(
      `/api/recipes/${id}${hard ? "?hard=true" : ""}`),
  duplicate: (id: string) => api.post<Recipe>(`/api/recipes/${id}/duplicate`),

  /**
   * The picture.
   *
   * Bytes live behind a bearer token, so `imageUrl` returns an object URL the
   * caller must revoke — same contract as CirclesApi.photoUrl. `setImage` takes
   * a downscaled data URL; `source` records which of the three capture paths
   * produced it, so the editor can say "the photo you took" vs "the one off the
   * web page".
   */
  imageUrl: (id: string) => api.objectUrl(`/api/recipes/${id}/image`),
  setImage: (id: string, dataUrl: string, source: "upload" | "scan" | "import" = "upload") =>
    api.put<{ ok: true }>(`/api/recipes/${id}/image`, { image: dataUrl, source }),
  clearImage: (id: string) => api.del<{ ok: true }>(`/api/recipes/${id}/image`),
  scaled: (id: string, servings: number) =>
    api.get<ScaledRecipe>(`/api/recipes/${id}/scaled?servings=${servings}`),

  /**
   * Recalculate the per-serving nutrition panel.
   *
   * There is deliberately no `getNutrition` to pair with this. The panel comes
   * down inside GET /api/recipes/:id, and a recipe that has never been
   * estimated starts one in the background as a side effect of being opened —
   * so the ordinary path needs no call at all. This route exists for the two
   * states that do not self-heal: an estimate that failed, and a panel the cook
   * has read and does not believe.
   */
  estimateNutrition: (id: string) =>
    api.post<{ nutrition: NutritionPanel; nutritionStatus: NutritionStatus }>(
      `/api/recipes/${id}/nutrition`),
  conflicts: (id: string) =>
    api.get<RecipeConflicts>(`/api/recipes/${id}/conflicts`),
  rate: (id: string, stars: number) => api.put(`/api/recipes/${id}/rating`, { stars }),
  // Household-PRIVATE notes. The cross-household conversation is ThreadsApi.
  comments: (id: string) =>
    api.get<{ comments: RecipeComment[] }>(`/api/recipes/${id}/comments`),
  comment: (id: string, body: string) =>
    api.post<RecipeComment>(`/api/recipes/${id}/comments`, { body }),
  // Note the shape: comments are deleted by their OWN id, not nested under the
  // recipe, because that's how the route is registered.
  deleteComment: (commentId: string) =>
    api.del<{ ok: true }>(`/api/recipes/comments/${commentId}`),

  search: (body: Record<string, unknown>) =>
    api.post<{ results: RecipeSummary[]; mode: string; semanticAvailable: boolean; total: number }>(
      "/api/recipes/search", body),
  similar: (id: string) => api.get<{ results: RecipeSummary[] }>(`/api/recipes/similar/${id}`),
  reindex: () => api.post<{ ok: boolean; embedded?: number; reason?: string }>("/api/recipes/reindex"),
};

/**
 * Recipe sharing between households.
 *
 * `preview` and `accept` POST the code in a body rather than a path or query
 * param on purpose: a share code is a secret, and secrets don't belong in
 * access logs, proxy logs or browser history.
 */
export const SharesApi = {
  create: (
    recipeIds: string[],
    opts: { message?: string; maxUses?: number; allowDiscussion?: boolean } = {},
  ) => api.post<CreatedShare>("/api/shares", { recipeIds, ...opts }),
  list: () => api.get<{ shares: OutgoingShare[] }>("/api/shares"),
  revoke: (id: string) =>
    api.del<{ ok: true; alreadyAcceptedCopiesRemain: boolean }>(`/api/shares/${id}`),
  received: () => api.get<{ received: IncomingShare[] }>("/api/shares/received"),
  preview: (code: string) => api.post<SharePreview>("/api/shares/preview", { code }),
  // `joinDiscussion` is a separate decision from taking the recipe, and saying
  // no here is not final — ThreadsApi.join reopens it later.
  accept: (code: string, joinDiscussion = false) =>
    api.post<AcceptedShare>("/api/shares/accept", { code, joinDiscussion }),
};

/**
 * Cross-household recipe discussion.
 *
 * Every call is addressed by OUR OWN recipe id, never by the thread's lineage —
 * the server does not put that identifier on the wire, so there is nothing here
 * that could name a conversation we don't hold a copy of.
 */
export const ThreadsApi = {
  state: (recipeId: string) =>
    api.get<ThreadAvailability>(`/api/recipes/${recipeId}/thread`),
  sync: (recipeId: string, since = 0) =>
    api.get<ThreadDelta>(`/api/recipes/${recipeId}/thread/sync?since=${since}`),
  list: () => api.get<{ threads: ThreadListEntry[] }>("/api/threads"),

  join: (recipeId: string) =>
    api.post<ThreadSummary>(`/api/recipes/${recipeId}/thread/join`, {}),
  leave: (recipeId: string, deleteMyComments = false) =>
    api.post<{ ok: true; commentsRemoved: number }>(
      `/api/recipes/${recipeId}/thread/leave`, { deleteMyComments },
    ),

  comment: (recipeId: string, body: string, replyTo?: string | null) =>
    api.post<ThreadComment>(
      `/api/recipes/${recipeId}/thread/comments`, { body, replyTo: replyTo ?? null },
    ),
  editComment: (recipeId: string, commentId: string, body: string) =>
    api.patch<{ ok: true; revision: number; editedAt: string }>(
      `/api/recipes/${recipeId}/thread/comments/${commentId}`, { body },
    ),
  deleteComment: (recipeId: string, commentId: string) =>
    api.del<{ ok: true; asModerator: boolean }>(
      `/api/recipes/${recipeId}/thread/comments/${commentId}`,
    ),
  flagComment: (recipeId: string, commentId: string, reason?: string) =>
    api.post<{ ok: true }>(
      `/api/recipes/${recipeId}/thread/comments/${commentId}/flag`, { reason },
    ),

  rate: (recipeId: string, stars: number) =>
    api.put<{ average: number | null; count: number; mine: number }>(
      `/api/recipes/${recipeId}/thread/rating`, { stars },
    ),
  markRead: (recipeId: string, revision: number) =>
    api.post<{ ok: true; lastReadRevision: number }>(
      `/api/recipes/${recipeId}/thread/read`, { revision },
    ),
  mute: (recipeId: string, muted: boolean) =>
    api.post<{ ok: true; isMuted: boolean }>(
      `/api/recipes/${recipeId}/thread/mute`, { muted },
    ),
  block: (recipeId: string, participantId: string, blocked: boolean) =>
    api.post<{ ok: true }>(
      `/api/recipes/${recipeId}/thread/block`, { participantId, blocked },
    ),
  lock: (recipeId: string, locked: boolean, reason?: string) =>
    api.post<{ ok: true; isLocked: boolean }>(
      `/api/recipes/${recipeId}/thread/lock`, { locked, reason },
    ),
};

export const AiApi = {
  generate: (body: Record<string, unknown>) =>
    api.post<{ draft: RecipeDraft; model: string; attempts: number }>("/api/recipes/generate", body),
  scan: (images: string[]) =>
    api.post<{ scanId: string; status: string }>("/api/recipes/scan", { images }),
  scanStatus: (id: string) =>
    api.get<{
      status: string; ocr_markdown: string | null; ocr_confidence: number | null;
      parsed_json: RecipeDraft | null; low_confidence_fields: string[];
      lowConfidence: boolean; error: string | null;
    }>(`/api/recipes/scan/${id}`),
  reparse: (id: string) => api.post<{ draft: RecipeDraft }>(`/api/recipes/scan/${id}/reparse`),
  discardScan: (id: string) => api.del(`/api/recipes/scan/${id}`),
  importUrl: (url: string) =>
    // `imageUrl` is WHERE the picture is, not the picture. The server fetches
    // and stores the bytes only when the recipe is actually saved, so an
    // abandoned import leaves nothing behind.
    api.post<{ draft: RecipeDraft; via: string; sourceUrl?: string; imageUrl?: string | null }>(
      "/api/recipes/import-url", { url }),
  substitutions: (body: Record<string, unknown>) =>
    api.post<{
      substitutions: Array<{ toName: string; ratioNote: string; source: string }>;
      via: string; filteredForAllergens: number;
    }>("/api/substitutions", body),
};

export const PlanApi = {
  range: (from?: YMD, to?: YMD) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const s = p.toString();
    return api.get<PlanResponse>(`/api/plan${s ? `?${s}` : ""}`);
  },
  today: () => api.get<{ date: YMD; entries: PlanEntry[] }>("/api/plan/today"),
  conflicts: (from: YMD, to: YMD) =>
    api.get<{ conflicts: PlanConflict[]; allergenCount: number; unknownCount: number }>(
      `/api/plan/conflicts?from=${from}&to=${to}`),
  add: (body: Record<string, unknown>) => api.post<PlanEntry>("/api/plan", body),
  update: (id: string, body: Record<string, unknown>) => api.patch<PlanEntry>(`/api/plan/${id}`, body),
  setGuests: (id: string, body: Record<string, unknown>) =>
    api.patch<{ entry: PlanEntry; effectiveServings: number }>(`/api/plan/${id}/guests`, body),
  remove: (id: string) => api.del(`/api/plan/${id}`),
  move: (id: string, toDate: YMD, toSlot?: string) =>
    api.post<PlanEntry>("/api/plan/move", { id, toDate, toSlot }),
  copy: (body: Record<string, unknown>) =>
    api.post<{ copied: number; skippedLeftovers: number }>("/api/plan/copy", body),
  clear: (dates: YMD[], slots?: string[]) =>
    api.post<{ deleted: number }>("/api/plan/clear", { dates, slots }),
  leftovers: (entryId: string, dates: YMD[], slotKey?: string) =>
    api.post<{ created: number }>("/api/plan/leftovers", { entryId, dates, slotKey }),

  templates: () => api.get<{ templates: Array<{ id: string; title: string; span_days: number; entry_count: number }> }>(
    "/api/plan/templates"),
  saveTemplate: (title: string, from: YMD, to: YMD) =>
    api.post<{ id: string; entries: number }>("/api/plan/templates", { title, from, to }),
  applyTemplate: (id: string, startDate: YMD, overwrite = false) =>
    api.post<{ created: number; skipped: number }>(
      `/api/plan/templates/${id}/apply`, { startDate, overwrite }),
  removeTemplate: (id: string) => api.del(`/api/plan/templates/${id}`),

  exportPdf: (from: YMD, to: YMD, style: "grid" | "detail" = "grid") =>
    api.download(`/api/plan/export.pdf?from=${from}&to=${to}&style=${style}`,
      `meal-plan-${from}-to-${to}.pdf`),
  exportIcs: (from: YMD, to: YMD, slot = "dinner") =>
    api.download(`/api/plan/export.ics?from=${from}&to=${to}&slot=${slot}`,
      `meal-plan-${from}-to-${to}.ics`),
};

export const ShoppingApi = {
  lists: (status?: string) =>
    api.get<{ lists: ShoppingList[] }>(`/api/shopping/lists${status ? `?status=${status}` : ""}`),
  get: (id: string) =>
    api.get<{ list: ShoppingList; items: ShoppingItem[]; aisleOrder: string[]; checkedCount: number; totalCount: number }>(
      `/api/shopping/lists/${id}`),
  generate: (body: Record<string, unknown>) =>
    api.post<{ list: ShoppingList; items: ShoppingItem[]; skippedStaples: string[]; skippedInStock: string[] }>(
      "/api/shopping/lists/generate", body),
  update: (id: string, body: Record<string, unknown>) =>
    api.patch<ShoppingList>(`/api/shopping/lists/${id}`, body),
  remove: (id: string) => api.del(`/api/shopping/lists/${id}`),

  /** What a regenerate WOULD change. Mutates nothing. */
  preview: (id: string) => api.post<{ diff: RegenerateDiff }>(`/api/shopping/lists/${id}/preview`),
  regenerate: (id: string, removeStale = false) =>
    api.post<{ added: number; updated: number; removed: number; removalsSkipped: number; items: ShoppingItem[] }>(
      `/api/shopping/lists/${id}/regenerate`, { removeStale }),

  addItem: (listId: string, body: Record<string, unknown>) =>
    api.post<ShoppingItem>(`/api/shopping/lists/${listId}/items`, body),
  updateItem: (id: string, body: Record<string, unknown>) =>
    api.patch<ShoppingItem>(`/api/shopping/items/${id}`, body),
  check: (id: string, checked: boolean) =>
    api.post<ShoppingItem>(`/api/shopping/items/${id}/check`, { checked }),
  removeItem: (id: string) => api.del(`/api/shopping/items/${id}`),
  sources: (id: string) => api.get<{ sources: ItemSource[] }>(`/api/shopping/items/${id}/sources`),
  complete: (id: string, carryUnchecked: boolean) =>
    api.post<{ uncheckedCount: number; carriedListId: string | null }>(
      `/api/shopping/lists/${id}/complete`, { carryUnchecked }),

  sync: (id: string, since: number) =>
    api.get<SyncResponse>(`/api/shopping/lists/${id}/sync?since=${since}`),
  mutations: (id: string, mutations: unknown[]) =>
    api.post<{
      revision: number;
      results: Array<{ clientMutationId: string; status: string; reason?: string }>;
      applied: number; duplicates: number; conflicts: number; rejected: number;
    }>(`/api/shopping/lists/${id}/mutations`, { mutations }),
  heartbeat: (id: string) => api.post(`/api/shopping/lists/${id}/presence`),

  exportPdf: (id: string, today: YMD) =>
    api.download(`/api/shopping/lists/${id}/export.pdf`, `shopping-list-${today}.pdf`),
};

export const PantryApi = {
  staples: () => api.get<{ staples: PantryStaple[] }>("/api/pantry/staples"),
  addStaple: (body: Record<string, unknown>) => api.post<PantryStaple>("/api/pantry/staples", body),
  updateStaple: (id: string, body: Record<string, unknown>) =>
    api.patch<PantryStaple>(`/api/pantry/staples/${id}`, body),
  removeStaple: (id: string) => api.del(`/api/pantry/staples/${id}`),
  suggestions: () =>
    api.get<{ suggestions: Array<{ food_id: string; display_name: string; uses: number }> }>(
      "/api/pantry/staples/suggest"),

  inventory: () => api.get<{ inventory: unknown[]; today: YMD }>("/api/pantry/inventory"),
  addInventory: (body: Record<string, unknown>) => api.post("/api/pantry/inventory", body),
  expiring: (days = 7) =>
    api.get<{ items: unknown[]; today: YMD }>(`/api/pantry/expiring?days=${days}`),

  barcode: (code: string) =>
    api.get<{
      found: boolean; product?: Record<string, unknown>;
      allergenWarnings?: Array<{ profileName: string; allergen: string }>;
      allergenNote?: string; message?: string;
    }>(`/api/barcode/${encodeURIComponent(code)}`),
  barcodeToList: (code: string, listId: string) =>
    api.post<{ item: ShoppingItem; found: boolean }>(
      `/api/barcode/${encodeURIComponent(code)}/to-list`, { listId }),
};

export const CookApi = {
  start: (recipeId: string, body: Record<string, unknown>) =>
    api.post<CookSession>(`/api/recipes/${recipeId}/cook/start`, body),
  open: () => api.get<{ sessions: CookSession[] }>("/api/cook/sessions/open"),
  get: (id: string) => api.get<CookSession>(`/api/cook/sessions/${id}`),
  progress: (id: string, body: Record<string, unknown>) =>
    api.patch<CookSession>(`/api/cook/sessions/${id}`, body),
  finish: (id: string, body: Record<string, unknown>) =>
    api.post<{ cookedOn: YMD; timesCooked: number; lastCookedLabel: string }>(
      `/api/cook/sessions/${id}/finish`, body),
  abandon: (id: string) => api.post(`/api/cook/sessions/${id}/abandon`),

  logCooked: (recipeId: string, body: Record<string, unknown>) =>
    api.post<{ cookedOn: YMD; lastCookedLabel: string }>(`/api/recipes/${recipeId}/cooked`, body),
  history: (recipeId: string) =>
    api.get<{ history: Array<{ cooked_on: YMD; label: string; rating: number | null; note: string | null }> }>(
      `/api/recipes/${recipeId}/history`),
  summary: (sort: "leastRecent" | "mostCooked" = "leastRecent") =>
    api.get<{ recipes: Array<RecipeSummary & { label: string; neverCooked: boolean }> }>(
      `/api/cook/summary?sort=${sort}`),
};

export const HomeApi = {
  get: () => api.get<HomeResponse>("/api/home"),
  /** Costs one AI call. Only ever from an explicit user action. */
  refreshBrief: () => api.post<{ brief: HomeBrief }>("/api/home/brief/refresh"),
};

export const CatalogApi = {
  /**
   * The allergen and diet lists both clients pick from.
   *
   * Cached hard server-side (an hour): it changes when food law does, and a
   * picker must not refetch it per keystroke.
   */
  dietOptions: (lang: string) =>
    api.get<DietOptionsResponse>(
      `/api/catalog/diet-options?lang=${encodeURIComponent(lang)}`),
};

/**
 * Circles: private chat between households.
 *
 * All endpoints require circles to be enabled in household settings.
 * The feature is OFF BY DEFAULT — a household must explicitly opt in.
 */
export const CirclesApi = {
  settings: {
    get: () => api.get<CirclesSettings>("/api/circles/settings"),
    set: (enabled: boolean) => api.post<CirclesSettings>("/api/circles/settings", { enabled }),
    // Sent alone: the route presence-checks each key, so including `enabled`
    // here would risk toggling circles as a side effect.
    setAutoAccept: (autoAcceptRecipes: boolean) =>
      api.post<CirclesSettings>("/api/circles/settings", { autoAcceptRecipes }),
    /** The group shown on Today. `null` returns it to most-recently-active. */
    setPinned: (circleId: string | null) =>
      api.post<{ pinnedCircleId: string | null }>(
        "/api/circles/settings/pinned", { circleId }),
  },

  // Circles
  create: (body: { name: string; retentionDays?: number }) =>
    api.post<Circle>("/api/circles", body),
  list: () => api.get<{ circles: CircleSummary[] }>("/api/circles"),
  get: (id: string) => api.get<Circle>(`/api/circles/${id}`),
  update: (id: string, body: { name?: string; retentionDays?: number; isLocked?: boolean }) =>
    api.patch<Circle>(`/api/circles/${id}`, body),
  remove: (id: string) => api.del<{ ok: true }>(`/api/circles/${id}`),
  leave: (id: string) => api.post<{ ok: true }>(`/api/circles/${id}/leave`),
  removeMember: (circleId: string, householdId: string) =>
    api.del<{ ok: true }>(`/api/circles/${circleId}/members/${householdId}`),

  // Members
  members: (id: string) => api.get<{ members: CircleMember[] }>(`/api/circles/${id}/members`),
  setDisplayLabel: (id: string, displayLabel: string) =>
    api.patch<{ ok: true }>(`/api/circles/${id}/members/me`, { displayLabel }),
  setMuted: (id: string, muted: boolean) =>
    api.patch<{ ok: true }>(`/api/circles/${id}/members/me/mute`, { muted }),
  markRead: (id: string, revision: number) =>
    api.post<{ ok: true }>(`/api/circles/${id}/members/me/read`, { revision }),

  // Invites
  createInvite: (id: string, maxUses?: number) =>
    api.post<CreatedInvite>(`/api/circles/${id}/invites`, { maxUses: maxUses ?? 1 }),
  listInvites: (id: string) => api.get<{ invites: CircleInvite[] }>(`/api/circles/${id}/invites`),
  revokeInvite: (circleId: string, inviteId: string) =>
    api.del<{ ok: true }>(`/api/circles/${circleId}/invites/${inviteId}`),
  redeemInvite: (code: string) => api.post<Circle>("/api/circles/invites/redeem", { code }),

  // Messages
  messages: (id: string, since?: number, limit?: number) => {
    const p = new URLSearchParams();
    if (since !== undefined) p.set("since", String(since));
    if (limit !== undefined) p.set("limit", String(limit));
    const s = p.toString();
    return api.get<{ messages: CircleMessage[]; revision: number }>(
      `/api/circles/${id}/messages${s ? `?${s}` : ""}`);
  },
  postMessage: (
    id: string,
    body: {
      body: string; suggestedTitle?: string; photos?: string[];
      /** Makes this a reply. One level only; the server refuses deeper. */
      replyTo?: string;
      /**
       * Tags a recipe already shared with this circle (043). Not a substitute
       * for body or photos, never on a reply — the server enforces both.
       */
      recipeId?: string;
    },
  ) => api.post<PostedMessage>(`/api/circles/${id}/messages`, body),

  /**
   * Add or remove one reaction.
   *
   * Idempotent in both directions, and returns the message's FULL reaction
   * summary rather than an ack — the client applies these optimistically, so
   * it needs the truth back to reconcile against.
   */
  react: (circleId: string, messageId: string, kind: ReactionKind, on: boolean) =>
    api.post<{ reactions: ReactionSummary[] }>(
      `/api/circles/${circleId}/messages/${messageId}/reactions`, { kind, on }),

  /** A post's replies, oldest first. Fetched on demand, not with the feed. */
  replies: (circleId: string, messageId: string) =>
    api.get<{ replies: CircleMessage[] }>(
      `/api/circles/${circleId}/messages/${messageId}/replies`),
  /** Object URL for one photo. The caller must revoke it. */
  photoUrl: (circleId: string, messageId: string, photoId: string) =>
    api.objectUrl(`/api/circles/${circleId}/messages/${messageId}/photos/${photoId}`),
  editMessage: (circleId: string, messageId: string, body: string) =>
    api.patch<{ ok: true }>(`/api/circles/${circleId}/messages/${messageId}`, { body }),
  deleteMessage: (circleId: string, messageId: string) =>
    api.del<{ ok: true }>(`/api/circles/${circleId}/messages/${messageId}`),
  flagMessage: (circleId: string, messageId: string, reason?: string) =>
    api.post<{ ok: true }>(`/api/circles/${circleId}/messages/${messageId}/flag`, { reason }),
  syncMessages: (id: string, since: number) =>
    api.get<CircleDelta>(`/api/circles/${id}/messages/sync?since=${since}`),

  // Blocks
  setBlocked: (id: string, body: { memberId: string; blocked: boolean }) =>
    api.post<{ ok: true }>(`/api/circles/${id}/blocks`, body),

  // Library sharing
  library: {
    settings: {
      get: (id: string) => api.get<CircleLibrarySettings>(`/api/circles/${id}/library-sharing`),
      // Enable is POST, disable is DELETE. Posting `{ enabled: false }` also
      // works server-side, but going through DELETE keeps the verb honest.
      set: (id: string, enabled: boolean) =>
        enabled
          ? api.post<CircleLibrarySettings>(`/api/circles/${id}/library-sharing`, { enabled: true })
          : api.del<CircleLibrarySettings>(`/api/circles/${id}/library-sharing`),
    },
    recipes: {
      // includeOwn asks for the whole shelf (own shares flagged isOwn) — the
      // strip and the tag picker want that; the accept browser must keep the
      // default accept-only list.
      list: (id: string, includeOwn = false) =>
        api.get<{ recipes: AvailableLibraryRecipe[]; total: number }>(
          `/api/circles/${id}/library-recipes${includeOwn ? "?includeOwn=true" : ""}`,
        ),
      accept: (circleId: string, recipeId: string) =>
        // The server returns the acceptor's NEW copy's identity (not a full
        // RecipeSummary) so the client can navigate straight to it.
        api.post<{ recipe: { id: string; title: string }; ok: true }>(`/api/circles/${circleId}/library-recipes/${recipeId}/accept`),
    },
  },
};
